import React, { useState, useEffect } from 'react';
import axios from 'axios';

const DocumentUpload = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('flight');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get('/documents');
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('document_type', documentType);

    try {
      const response = await axios.post('/documents/upload', formData);
      
      // Refresh documents list
      fetchDocuments();
      
      // Reset form
      setSelectedFile(null);
      e.target.reset();
      
      alert('Document uploaded successfully! AI parsing has started.');
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Error uploading document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDocumentIcon = (type) => {
    switch (type) {
      case 'flight': return '✈️';
      case 'hotel': return '🏨';
      case 'car_rental': return '🚗';
      case 'restaurant': return '🍽️';
      case 'event': return '🎫';
      case 'meeting': return '👥';
      case 'speaker_meeting': return '🎤';
      default: return '📄';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Document Upload</h1>
        <p className="mt-1 text-sm text-gray-500">
          Upload travel confirmations for AI parsing and automatic data extraction
        </p>
      </div>

      {/* Upload Form */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Upload New Document</h3>
        <form onSubmit={handleUpload} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Type
            </label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="flight">Flight Confirmation</option>
              <option value="hotel">Hotel Confirmation</option>
              <option value="car_rental">Car Rental Confirmation</option>
              <option value="restaurant">Restaurant Reservation</option>
              <option value="event">Event Ticket</option>
              <option value="meeting">Meeting Confirmation</option>
              <option value="speaker_meeting">Speaker Meeting Confirmation</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload File
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <div className="text-center">
                <span className="text-4xl">📄</span>
                <div className="mt-4">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      {selectedFile ? selectedFile.name : 'Choose file to upload'}
                    </span>
                    <span className="mt-1 block text-xs text-gray-500">
                      PDF, DOC, DOCX, TXT, JPG, PNG up to 10MB
                    </span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={!selectedFile || uploading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            {uploading ? 'Uploading & Processing...' : 'Upload Document'}
          </button>
        </form>
      </div>

      {/* Documents List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Uploaded Documents
          </h3>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8">
              <span className="text-4xl">📄</span>
              <p className="mt-2 text-gray-500">No documents uploaded yet</p>
              <p className="text-sm text-gray-400">Upload your first travel confirmation above</p>
            </div>
          ) : (
            <div className="space-y-4">
              {documents.map((doc) => (
                <div key={doc.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-xl">{getDocumentIcon(doc.document_type)}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.file_name}</p>
                        <p className="text-xs text-gray-500 capitalize">
                          {doc.document_type.replace('_', ' ')} • {new Date(doc.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(doc.processing_status)}`}>
                        {doc.processing_status}
                      </span>
                    </div>
                  </div>
                  
                  {doc.parsed_data && doc.parsed_data.success && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-md">
                      <p className="text-xs font-medium text-gray-700 mb-2">Extracted Data:</p>
                      <pre className="text-xs text-gray-600 whitespace-pre-wrap max-h-32 overflow-y-auto">
                        {JSON.stringify(doc.parsed_data.data, null, 2)}
                      </pre>
                    </div>
                  )}
                  
                  {doc.parsed_data && !doc.parsed_data.success && (
                    <div className="mt-3 p-3 bg-red-50 rounded-md">
                      <p className="text-xs font-medium text-red-700 mb-1">Parsing Error:</p>
                      <p className="text-xs text-red-600">{doc.parsed_data.error}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;